/* eslint-disable no-console */
import {
  AMPLIFY_USER_POOL_API_CLIENT_ID,
  AMPLIFY_USER_POOL_API_CLIENT_SECRET,
  COGNITO_OAUTH_DOMAIN,
} from 'src/config/config'
import { ensureValidIdToken } from 'src/hooks/tokenRefreshGuard'
import { logoutAndRedirect } from 'src/utils/authLogout'

export const getUserInfo = async () => {
  try {
    const validIdToken = await ensureValidIdToken() // refresh if needed
    if (!validIdToken) {
      // ensureValidIdToken() already triggered logout (cleared tokens and emitted event)
      // Redirect to signin (event listener in _app.tsx will handle Redux)
      if (typeof window !== 'undefined') {
        await logoutAndRedirect('getUserInfo: Token refresh failed', '/signin')
      }
      return null
    }

    const accessToken = localStorage.getItem('accessToken')
    if (!accessToken) {
      console.error('No access token found')
      return null
    }

    const response = await fetch(`https://${COGNITO_OAUTH_DOMAIN}/oauth2/userInfo`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      // Only clear auth on 401/403, not on other errors (could be network issues)
      if (response.status === 401 || response.status === 403) {
        // Use centralized logout function (event listener in _app.tsx will handle Redux)
        if (typeof window !== 'undefined') {
          await logoutAndRedirect('getUserInfo: 401/403 response', '/signin')
        }
      }
      console.error('getUserInfo failed:', response.status, response.statusText)
      return null
    }

    return await response.json()
  } catch (error) {
    // Network errors shouldn't trigger logout - just return null
    console.error('Error in getUserInfo:', error)
    return null
  }
}

export const refreshTokenApi = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken')
    if (!refreshToken) {
      // eslint-disable-next-line no-console
      console.error('No refresh token found in localStorage')
      return null
    }

    const response = await fetch(`https://${COGNITO_OAUTH_DOMAIN}/oauth2/token`, {
      method: 'POST',
      headers: {
        Authorization: `Basic ${Buffer.from(
          `${AMPLIFY_USER_POOL_API_CLIENT_ID}:${AMPLIFY_USER_POOL_API_CLIENT_SECRET}`,
        ).toString('base64')}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      }),
    })

    if (!response.ok) {
      // Check if refresh token is expired (400/401) vs other errors
      const isTokenExpired = response.status === 400 || response.status === 401
      // eslint-disable-next-line no-console
      console.error(
        `Token refresh failed: ${response.status} ${response.statusText}`,
        isTokenExpired ? '(Refresh token expired or invalid)' : '(Network or server error)',
      )
      return null
    }

    const result = await response.json()

    // Validate response has required tokens
    if (!result?.id_token || !result?.access_token) {
      // eslint-disable-next-line no-console
      console.error('Token refresh response missing required tokens')
      return null
    }

    return result
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error in refreshTokenApi:', error)
    return null
  }
}
